<template>
  <b-modal
    v-model="localShowDetailModal"
    :title="'Detail Competency TNA'"
    size="xl"
    hide-footer
    @hide="$emit('close')"
  >
    <div v-if="isLoadingModal" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading data...</p>
    </div>
    <div v-if="!isLoadingModal">
      <div class="competency-modal-toolbar">
        <div>
          <h3 v-if="listCompetencies?.length > 0" class="mb-1">Get List Course by Competency</h3>
          <small class="text-muted">
            {{ filteredCompetencies.length }} dari {{ sortedCompetencies.length }} course tersedia
          </small>
        </div>
        <div class="competency-filter-controls">
          <b-form-input
            v-model.trim="searchQuery"
            size="sm"
            placeholder="Cari course, provider, platform..."
          />
          <b-form-select
            v-model="platformFilter"
            size="sm"
            :options="platformOptions"
          />
          <b-form-select
            v-model="statusFilter"
            size="sm"
            :options="statusOptions"
          />
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            :disabled="!searchQuery && !platformFilter && !statusFilter"
            @click="clearFilters"
          >
            Clear
          </button>
        </div>
      </div>
      <CardGrid
        :isCanSelect="true"
        :isLoading="isLoadingModal"
        :selectedItems="selectedItems"
        :type="'structured'"
        :items="paginatedCompetencies"
        @open-detail="handleOpenModalDetail"
        @select-change="$emit('select-change', ...arguments)"
      />
      <div
        v-if="filteredCompetencies.length > 0"
        class="competency-pagination"
      >
        <div class="d-flex align-items-center gap-2">
          <small class="text-muted">Show</small>
          <b-form-select
            v-model="perPage"
            size="sm"
            :options="perPageOptions"
            class="competency-page-size"
          />
          <small class="text-muted">entries</small>
        </div>
        <b-pagination
          v-model="currentPage"
          :total-rows="filteredCompetencies.length"
          :per-page="perPage"
          size="sm"
          align="right"
          class="mb-0"
        />
      </div>
    </div>
  </b-modal>
</template>

<script>
import CardGrid from "../components/card-grid.vue";
import { getApprovalStatusLabel } from "@/utils/approvalStatus";

export default {
  name: "DetailTNA",
  components: { CardGrid },
  props: {
    selectedItems: {
      type: Array,
      default: () => [],
    },
    isLoadingModal: {
      type: Boolean,
      default: false,
    },
    showDetailModalCompetency: {
      type: Boolean,
      default: false,
    },
    listCompetencies: {
      type: Array,
      default: () => null,
    },
    handleOpenModalDetail: Function,
  },
  computed: {
    localShowDetailModal: {
      get() {
        return this.showDetailModalCompetency;
      },
      set(value) {
        if (!value) {
          this.$emit("close");
        }
      },
    },
    platformOptions() {
      const platforms = [...new Set((this.listCompetencies || []).map((item) => item.platform).filter(Boolean))];
      return [
        { value: null, text: "All Platform" },
        ...platforms.map((platform) => ({ value: platform, text: platform })),
      ];
    },
    statusOptions() {
      const statuses = [...new Set((this.listCompetencies || []).map((item) => this.normalizeStatus(item.status)).filter(Boolean))];
      return [
        { value: null, text: "All Status" },
        ...statuses
          .sort((a, b) => this.getStatusRank(a) - this.getStatusRank(b) || this.getStatusLabel(a).localeCompare(this.getStatusLabel(b)))
          .map((status) => ({ value: status, text: this.getStatusLabel(status) })),
      ];
    },
    sortedCompetencies() {
      return [...(this.listCompetencies || [])].sort((a, b) => {
        const statusRank = this.getStatusRank(a.status) - this.getStatusRank(b.status);
        if (statusRank !== 0) return statusRank;

        const dateA = new Date(a.updated_at || a.created_at || 0).getTime() || 0;
        const dateB = new Date(b.updated_at || b.created_at || 0).getTime() || 0;
        if (dateA !== dateB) return dateB - dateA;

        return String(a.title || "").localeCompare(String(b.title || ""));
      });
    },
    filteredCompetencies() {
      const search = this.searchQuery.toLowerCase();

      return this.sortedCompetencies.filter((item) => {
        const matchPlatform = !this.platformFilter || item.platform === this.platformFilter;
        const matchStatus = !this.statusFilter || this.normalizeStatus(item.status) === this.statusFilter;
        const source = [
          item.title,
          item.description,
          item.provider_name,
          item.platform,
          item.type_name,
          item.training_type,
        ].filter(Boolean).join(" ").toLowerCase();
        const matchSearch = !search || source.includes(search);

        return matchPlatform && matchStatus && matchSearch;
      });
    },
    paginatedCompetencies() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredCompetencies.slice(start, start + this.perPage);
    },
  },
  data() {
    return {
      searchQuery: "",
      platformFilter: null,
      statusFilter: null,
      currentPage: 1,
      perPage: 12,
      perPageOptions: [
        { value: 8, text: "8" },
        { value: 12, text: "12" },
        { value: 24, text: "24" },
        { value: 48, text: "48" },
      ],
    };
  },
  watch: {
    showDetailModalCompetency(value) {
      if (value) this.clearFilters();
    },
    searchQuery() {
      this.currentPage = 1;
    },
    platformFilter() {
      this.currentPage = 1;
    },
    statusFilter() {
      this.currentPage = 1;
    },
    perPage() {
      this.currentPage = 1;
    },
    listCompetencies: {
      handler() {
        this.currentPage = 1;
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    getApprovalStatusLabel,
    clearFilters() {
      this.searchQuery = "";
      this.platformFilter = null;
      this.statusFilter = null;
      this.currentPage = 1;
    },
    normalizeStatus(status) {
      return String(status || "open").trim().toLowerCase();
    },
    getStatusLabel(status) {
      return this.getApprovalStatusLabel(status);
    },
    getStatusRank(status) {
      const normalized = this.normalizeStatus(status);
      const ranks = {
        approval_manager: 1,
        approval_hse: 2,
        approval_hr: 2,
        approved: 3,
        rejected: 4,
        open: 99,
      };

      return ranks[normalized] || (normalized === "open" ? 99 : 10);
    },
  },
};
</script>

<style scoped>
.competency-modal-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.competency-filter-controls {
  display: grid;
  grid-template-columns: minmax(220px, 320px) minmax(140px, 180px) minmax(140px, 180px) auto;
  gap: 8px;
  align-items: center;
}

.competency-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
}

.competency-page-size {
  width: 76px;
}

@media (max-width: 768px) {
  .competency-modal-toolbar {
    display: block;
  }

  .competency-filter-controls {
    grid-template-columns: 1fr;
    margin-top: 12px;
  }

  .competency-pagination {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
